import { useCallback, useEffect, useState } from "react";
import {
  Building03Icon,
  ComputerTerminal01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { RouterOutputs } from "@degaus/trpc";
import { formatCurrencyInCents } from "@degaus/shared/utils";

import type { IconSvgElement } from "@hugeicons/react";
import { appendSubscriptionSuccessParams } from "@/common/google-ads";
import { GLASS_POPULAR_LIGHT, GLASS_RAISED_LIGHT } from "@/common/ui/glass";
import { cn } from "@/common/utils";
import { Button } from "@/components/ui/Button";
import { useSessionAuthState } from "@/hooks/useSession";
import { SIGNUPS_GATED } from "@/landing/lib/constants";
import { CheckBullet } from "@/landing/ui/CheckBullet";
import { SectionEyebrow } from "@/landing/ui/SectionEyebrow";
import { api } from "@/trpc/react";
import { DigitLogo } from "./DigitLogo";
import { OutboundCtaLink } from "./OutboundCtaLink";

type PublicPlanRow = RouterOutputs["payments"]["listPublicPlans"][number];

// Signed-in upgrade context lifted from the client-only resolver: drives the
// current-plan highlight and routes the CTA to a Stripe checkout instead of /signup.
type AccountUpgrade = {
  isSignedIn: boolean;
  currentPlanCode: string | null;
  /** The price the account actually pays, which can be an older price. */
  currentPlanPrice: CurrentPlanPrice | null;
  upgrade: (planCode: string) => void;
  isUpgrading: boolean;
};

type CurrentPlanPrice = Pick<
  PublicPlanRow,
  "unitPriceCents" | "currency" | "interval"
>;

type CliPlan = {
  /**
   * Plan code this card maps to. Resolves the price from the DB plan catalog
   * (billing_plan) by code and highlights the signed-in user's tier. The title
   * + bullets are hardcoded marketing copy (not the DB label).
   */
  planCode: string;
  /** Marketing card title (overrides the DB plan label). */
  title: string;
  /** Green-accent icon rendered above the tier name. */
  icon: IconSvgElement;
  /** Fallback price shown until the DB price loads (or if the plan is missing). */
  price: string;
  period: string;
  popular?: boolean;
  ctaKey: string;
  bullets: string[];
};

// The one plan new users can buy. Premium (WONDA_PREMIUM, cloud twins) is
// parked: existing subscribers keep it, but it is no longer sold here.
const PRO_PLAN: CliPlan = {
  planCode: "WONDA",
  title: "Pro",
  icon: ComputerTerminal01Icon,
  price: "$49.99",
  period: "monthly",
  popular: true,
  ctaKey: "getPro",
  bullets: [
    "#1 anti-detect tailored to social media",
    "Unlimited actions (reads, writes, outreach)",
    "Access full skill file library",
    "Connect 1 account, then $10/mo per account",
    "AI generation billed per use at cost, no credits included",
  ],
};

const CLI_PLANS: CliPlan[] = [PRO_PLAN];

// Green-accent tile that fronts each tier name, matching the Wab2 use-case
// icons (faint --green wash + the green mark).
function PlanIcon({ icon }: { icon: IconSvgElement }) {
  return (
    <span
      aria-hidden
      className="mb-4 inline-flex size-11 items-center justify-center text-[var(--green)]"
      style={{
        backgroundColor: "color-mix(in srgb, var(--green) 12%, transparent)",
      }}
    >
      <HugeiconsIcon icon={icon} className="size-6" strokeWidth={1.6} />
    </span>
  );
}

type CliPricingProps = {
  headingLevel?: "h1" | "h2";
  isStandalone?: boolean;
};

export function Wab2CliPricing({
  headingLevel = "h2",
  isStandalone = false,
}: CliPricingProps) {
  return (
    <section id="pricing" className="scroll-mt-16 px-4 py-20 sm:px-6 md:py-28">
      <motion.div
        className="mx-auto max-w-6xl"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <CliPricingTiers
          headingLevel={headingLevel}
          isStandalone={isStandalone}
        />
      </motion.div>
    </section>
  );
}

function CliPricingTiers({
  headingLevel,
  isStandalone,
}: Required<CliPricingProps>) {
  const { t } = useTranslation();
  const Heading = headingLevel;

  // The account context and DB plan catalog are resolved client-side only: the
  // tRPC + session providers mount on the client (see ClientProviders), so reading
  // any query or auth during the SPA shell prerender would throw. Until they
  // resolve, cards show the code-side label + hardcoded price fallback, nothing
  // is highlighted, and the CTA points anonymous-style to /signup.
  const [account, setAccount] = useState<AccountUpgrade | null>(null);
  const [publicPlans, setPublicPlans] = useState<Map<
    string,
    PublicPlanRow
  > | null>(null);
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  const currentPlanCode = account?.currentPlanCode ?? null;

  return (
    <>
      {isClient && !isStandalone && (
        <AccountUpgradeResolver onResolve={setAccount} />
      )}
      {isClient && <PublicPlanResolver onResolve={setPublicPlans} />}
      <div className="mb-12 text-center">
        <div className="flex justify-center">
          <SectionEyebrow index="04">Pricing</SectionEyebrow>
        </div>
        <Heading
          className="mt-5 text-2xl font-semibold tracking-[-0.03em] text-balance sm:text-3xl lg:text-4xl"
          style={{ color: "var(--cli-text)" }}
        >
          Unlimited access, one fixed cost
        </Heading>
        <p
          className="mt-3 text-base"
          style={{ color: "var(--cli-text-muted)" }}
        >
          {t("pricing.tiers.upgradeForQuality")}
        </p>
      </div>

      <div className="mx-auto grid max-w-sm grid-cols-1 gap-6 sm:max-w-3xl sm:grid-cols-2">
        {CLI_PLANS.map((plan) => {
          const isCurrent = plan.planCode === currentPlanCode;
          // DB catalog is the source of truth for the title + price; fall back
          // to the code label and hardcoded price while loading or if missing.
          const dbPlan = publicPlans?.get(plan.planCode);
          const title = plan.title;
          // Cards label the price "/mo", so only show the DB amount when it is
          // the monthly row. A plan with only a yearly price falls back to the
          // hardcoded monthly amount rather than rendering the yearly figure as
          // monthly.
          // A subscriber's own card shows what they pay: an existing
          // subscriber can be on an older price than new users are offered.
          const ownPrice = isCurrent ? account?.currentPlanPrice : null;
          const shownPrice =
            ownPrice?.interval === "month"
              ? ownPrice
              : dbPlan?.interval === "month"
                ? dbPlan
                : null;
          const price = shownPrice
            ? formatCurrencyInCents(
                shownPrice.unitPriceCents,
                shownPrice.currency,
                {
                  minimumFractionDigits: 2,
                },
              )
            : plan.price;
          const ctaLabel = t(`pricing.tiers.ctas.${plan.ctaKey}`);
          const ctaClassName = cn(
            "mt-6 w-full",
            // Landing-only: lift the steward-ghost label off the washed-out
            // --steward-muted so the secondary CTA reads as enabled, not
            // disabled (scoped here so settings/billing ghost buttons are
            // untouched).
            !plan.popular &&
              "text-[var(--cli-text-secondary)] hover:text-[var(--ink)]",
          );
          const ctaVariant = plan.popular ? "steward" : "steward-ghost";
          // One plan to sell, so no "most popular" badge: only flag the
          // signed-in user's current plan.
          const badgeLabel = isCurrent ? t("pricing.tiers.currentPlan") : null;
          return (
            <div key={plan.planCode} className="relative flex h-full flex-col">
              {badgeLabel && (
                <div className="absolute -top-3.5 left-1/2 z-10 -translate-x-1/2">
                  <span
                    className="rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap text-white"
                    style={{ backgroundColor: "var(--green)" }}
                  >
                    {badgeLabel}
                  </span>
                </div>
              )}
              <div
                className={cn(
                  "flex h-full flex-col overflow-hidden rounded-xl px-(--cli-card-gutter) py-8",
                  isCurrent && "ring-2 ring-[var(--cli-accent)]",
                )}
                style={plan.popular ? GLASS_POPULAR_LIGHT : GLASS_RAISED_LIGHT}
              >
                <PlanIcon icon={plan.icon} />
                <h3
                  className="text-xl font-bold md:min-h-[3.5rem]"
                  style={{ color: "var(--cli-text)" }}
                >
                  {title}
                </h3>

                <div className="mt-4 flex items-baseline gap-1">
                  <span
                    className="text-3xl font-semibold"
                    style={{ color: "var(--cli-text)" }}
                  >
                    {price}
                  </span>
                  {plan.period && (
                    <span
                      className="text-sm"
                      style={{ color: "var(--cli-text-muted)" }}
                    >
                      {t(`pricing.tiers.periods.${plan.period}`)}
                    </span>
                  )}
                </div>
                {isCurrent ? (
                  <Button
                    type="button"
                    variant={ctaVariant}
                    size="md"
                    disabled
                    className="mt-6 w-full"
                  >
                    {t("pricing.tiers.currentPlan")}
                  </Button>
                ) : account?.isSignedIn ? (
                  // Signed-in: start a Stripe checkout for this tier in place
                  // instead of bouncing to /account.
                  <Button
                    type="button"
                    variant={ctaVariant}
                    size="md"
                    onClick={() => account.upgrade(plan.planCode)}
                    disabled={account.isUpgrading}
                    className={ctaClassName}
                  >
                    {ctaLabel}
                  </Button>
                ) : (
                  <Button
                    asChild
                    variant={ctaVariant}
                    size="md"
                    className={ctaClassName}
                  >
                    {/* Wonda owns identity and billing for Wonda Twin. */}
                    <OutboundCtaLink placement="pricing">
                      {SIGNUPS_GATED ? t("waitlist.joinCta") : ctaLabel}
                    </OutboundCtaLink>
                  </Button>
                )}

                <ul className="mt-6 flex flex-col gap-3">
                  {plan.bullets.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckBullet />
                      <span
                        className="text-sm"
                        style={{ color: "var(--cli-text-secondary)" }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
        <div
          className="flex h-full flex-col overflow-hidden rounded-xl px-(--cli-card-gutter) py-8"
          style={GLASS_RAISED_LIGHT}
        >
          <PlanIcon icon={Building03Icon} />
          <h3
            className="text-xl font-bold md:min-h-[3.5rem]"
            style={{ color: "var(--cli-text)" }}
          >
            Custom
          </h3>

          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--cli-text-secondary)" }}
          >
            {t("pricing.tiers.customQuestion")}
          </p>

          <Button
            asChild
            variant="steward-ghost"
            size="md"
            className="mt-6 w-full text-[var(--cli-text-secondary)] hover:text-[var(--ink)]"
          >
            <a href="mailto:contact@wonda.sh">
              {t("pricing.tiers.ctas.contactUs")}
            </a>
          </Button>

          {/* Quiet bottom-anchored brand mark instead of a big centered void. */}
          <div className="mt-auto flex items-center pt-8">
            <DigitLogo variant="hero" light className="h-8 opacity-55" />
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Reads the signed-in account's auth + current plan and exposes an upgrade
 * action, lifting them up so cards can highlight the current tier and route the
 * CTA to a Stripe checkout. Renders nothing. Only mounted on the client (auth +
 * the tRPC provider), and the plan query stays disabled for anonymous visitors
 * so it never fires a 401.
 */
function AccountUpgradeResolver({
  onResolve,
}: {
  onResolve: (account: AccountUpgrade) => void;
}) {
  const { isSignedIn } = useSessionAuthState();
  const { data } = api.payments.getCurrentAccountPlan.useQuery(undefined, {
    enabled: isSignedIn === true,
    staleTime: 60 * 1000,
  });
  const { data: planDetails } =
    api.payments.getCurrentAccountPlanDetails.useQuery(undefined, {
      enabled: isSignedIn === true,
      staleTime: 60 * 1000,
    });
  // Destructure `mutate` (referentially stable across renders) instead of
  // holding the whole mutation result: `upgrade` is a useEffect dependency
  // below, so closing over the per-render mutation object would give `upgrade` a
  // new identity every render, re-run the effect every render, and blow React's
  // update-depth limit.
  const { mutate: changePlan, isPending: isUpgrading } =
    api.billing.changePlan.useMutation({
      onSuccess: (result) => window.location.assign(result.url),
      onError: (error) => toast.error(error.message),
    });

  // Gate the resolved plan on auth, not just the query: disabling the query
  // keeps its cache, so a stale entry from a previous session would otherwise
  // highlight a plan for a signed-out (or still-loading) visitor.
  const currentPlanCode = isSignedIn === true ? (data?.planCode ?? null) : null;
  const currentPlanPrice =
    isSignedIn === true && planDetails?.planCode === currentPlanCode
      ? planDetails
      : null;

  const upgrade = useCallback(
    (planCode: string) => {
      changePlan({
        planCode,
        interval: "month",
        successUrl: appendSubscriptionSuccessParams(
          `${window.location.origin}/account`,
        ),
        cancelUrl: `${window.location.origin}/account`,
      });
    },
    [changePlan],
  );

  useEffect(() => {
    onResolve({
      isSignedIn: isSignedIn === true,
      currentPlanCode,
      currentPlanPrice,
      upgrade,
      isUpgrading,
    });
  }, [
    isSignedIn,
    currentPlanCode,
    currentPlanPrice,
    upgrade,
    isUpgrading,
    onResolve,
  ]);

  return null;
}

/**
 * Reads the public plan catalog (billing_plan) and lifts a planCode -> plan map
 * up so each card can show the DB label + price. Renders nothing. Only mounted
 * on the client (the tRPC provider isn't present during the SPA shell prerender).
 */
function PublicPlanResolver({
  onResolve,
}: {
  onResolve: (plans: Map<string, PublicPlanRow>) => void;
}) {
  const { data } = api.payments.listPublicPlans.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data) return;
    // Cards bill monthly, so key each plan to its monthly price when present.
    const byCode = new Map<string, PublicPlanRow>();
    for (const plan of data) {
      const existing = byCode.get(plan.planCode);
      if (!existing || plan.interval === "month") {
        byCode.set(plan.planCode, plan);
      }
    }
    onResolve(byCode);
  }, [data, onResolve]);

  return null;
}
